import { t } from '@lingui/macro';

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  app: {
    buildHash: null,
  },
  carts: {
    currentCart: undefined,
    list: [],
  },
  catalog: {
    categories: [],
    families: [],
    insuranceOptions: [],
    allProductsFetched: false,
  },
  contract: {
    customer: { firstName: '', lastName: '' },
    steps: {
      termsAndConditions: 'PENDING',
      creditSign: 'PENDING',
      deviceReference: 'PENDING',
      handOverDevice: 'PENDING',
    },
    deviceReference: null,
  },
  customer: {
    deviceReference: '',
    email: '',
    firstName: '',
    lastName: '',
    options: [
      {
        enabled: true,
        id: 'sms',
        name: t({ id: 'bank.link.delivery.sms', message: `Send SMS` }),
        selected: true,
        type: 'phone',
      },
      {
        enabled: true,
        id: 'email',
        name: t({ id: 'bank.link.delivery.email', message: `Send Email` }),
        selected: false,
        type: 'email',
      },
    ],
    mobilePhone: '',
    phone: '',
    sequence: '',
    applyForFinance: true,
    orderProductsFromTD: true,
    isNewCustomer: true,
    company: {},
  },
  dashboard: {
    activeUsers: [],
    apiCalls: [],
    apiErrors: [],
    counters: [],
    dailySales: [],
    date: undefined,
    monthlySales: [],
    sales: [],
    socketStatus: 'Disconnected',
    storeLogins: [],
    today: undefined,
    unfinishedCarts: [],
    userErrors: [],
    uid: '',
    wls: [],
  },
  online: {
    families: [],
    selected: {
      extras: [],
      insurances: [],
      specialOffers: [],
      page: 0,
      isDeviceRelated: true,
    },
    extras: [],
    insurances: [],
    specialOffers: [],
    contractSummary: {},
    customer: {},
    order: { extras: [], specialOffers: [], insurances: [], shippingCosts: [] },
  },
  reports: {
    list: [],
    settlements: {
      payouts: [],
    },
  },
  pendings: {
    list: [],
    sale: {},
    filters: {},
    byId: {},
  },
  sales: {
    list: [],
    sale: {},
    filters: {},
    byId: {},
    lastAction: 'filter',
  },
  ui: {
    button: {
      loading: false,
    },
    loading: false,
    loaderType: 'loading',
    notifications: [],
    openModal: null,
    pageName: 'Home',
    prevLocation: '',
  },
  user: {
    name: '',
    email: '',
    loginStatus: null,
    role: null,
    selectedStore: null,
    session_token: null,
    stores: [],
  },
  whitelabel: {
    configs: {
      availableLocales: ['en', 'nb'],
      canAddExtras: 'CATALOG',
      canChangeLanguage: false,
      canCustomize: 'CATALOG',
      canFinance: 'ACTIVE',
      canTradeIn: 'ACTIVE',
      canUpgrade: 'ACTIVE',
      canUploadAccessoriesByCSV: true,
      canUploadDevicesByCSV: true,
      giftCardImgUrl: '',
      hasSingleSignOn: false,
      returnPaymentTypes: [],
      showAccessoriesAdminPage: true,
      standardName: 'Financing',
      swapName: 'Swap',
      swapPaymentTypes: [],
      tradeinName: 'Trade In',
      upgradeName: 'Easy Upgrade',
    },
    language: 'en',
    plans: [],
    subscriptions: [],
    translations: [],
  },
  recycle: {
    cart: undefined,
    checkmendOk: undefined,
    deviceReference: undefined,
    devices: [],
    fmi: undefined,
    orderUUID: undefined,
    selectedDevice: {
      evaluation: {},
      deviceID: undefined,
      deviceCode: undefined,
      questionsTree: {},
      diagnostic: {},
    },
    selectedDevices: [],
    selectedOutcome: undefined,
    type: undefined,
    receipt: {},
    tradecartStatus: {
      isCustomerApproved: 'PENDING',
      hasIdPhoto: 'PENDING',
    },
    funder: undefined,
    referenceType: undefined,
  },
};

export const reduxSlice = createSlice({
  name: 'redux',
  initialState: initialState,
  reducers: {
    changeSelectedStore: (state, action) => {
      state.user.selectedStore = action.payload;
    },
    login: (state, action) => {
      state.user = action.payload;
    },
    logout: (state, _action) => {
      state.user = initialState.user;
    },
    changeLanguage: (state, action) => {
      state.whitelabel.language = action.payload;
    },
    changeWhitelabel: (state, action) => {
      state.whitelabel = action.payload;
    },
    // keep it until posale migration is complete
    resetRecycle: (state) => {
      state.recycle = { ...initialState.recycle };
    },
    resetRecycleAndContract: (state) => {
      state.recycle = { ...initialState.recycle };
      state.contract = { ...initialState.contract };
    },
    resetRecycleAndCustomer: (state) => {
      state.recycle = { ...initialState.recycle };
      state.customer = { ...initialState.customer };
    },
    setOnlineTradeinQuote: (state, action) => {
      state.recycle.onlineQuote = action.payload;
      state.recycle.deviceReference = action.payload.devices[0].deviceReference;
      state.recycle.selectedDevice = {
        ...action.payload.devices[0],
      };
      state.recycle.useDiagnosticsMinval = action.payload.useDiagnosticsMinval;
      state.customer = {
        ...action.payload.customer,
        options: initialState.customer.options,
      };
    },
    setType: (state, action) => {
      state.recycle.type = action.payload.type;
      state.recycle.orderUUID = action.payload.orderUUID;
      state.recycle.funder = action.payload.funder;
      state.recycle.deviceReference = action.payload.deviceReference;
      state.recycle.referenceType = action.payload.referenceType;
    },
    setCriteria: (state, action) => {
      state.recycle.selectedDevice = {
        ...state.recycle.selectedDevice,
        questionsTree: { ...action.payload },
      };
    },
    setDevice: (state, action) => {
      const nextId =
        state.recycle.selectedDevices &&
        state.recycle.selectedDevices.length > 0
          ? Math.max(...state.recycle.selectedDevices.map((d) => d?.id ?? 0)) +
            1
          : 1;
      state.recycle.selectedDevices = [
        ...state.recycle.selectedDevices,
        {
          ...action.payload,
          id: state.recycle.selectedDevices.length === 0 ? 1 : nextId + 1,
        },
      ];
      state.recycle.devices =
        state.recycle.deviceReference && state.recycle.devices?.length === 1
          ? []
          : state.recycle.devices;
    },
  },
});

export const {
  changeSelectedStore,
  logout,
  login,
  changeLanguage,
  changeWhitelabel,
  setOnlineTradeinQuote,
  setType,
  setCriteria,
  setDevice,
  resetRecycle,
  resetRecycleAndContract,
  resetRecycleAndCustomer,
} = reduxSlice.actions;

export default reduxSlice.reducer;
